import { makeStyles } from '@material-ui/core/styles';

const styles = (theme) => ({
  sectionHeader: {
    marginBottom: 22
  },
  timelineSubtitle: {
    color: theme.colorPalette.green_100,
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'uppercase',
    marginBottom: 8,
    marginTop: 3
  },
  versionsListItem: {
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    paddingLeft: 8
  },
  valueFieldInline: {
    marginBottom: 0,
    display: 'inline-block'
  },
  icon: {
    height: 24,
    width: 24,
    margin: '0px 8px',
    verticalAlign: 'bottom'
  },
  timesIcon: {
    composes: '$icon'
  },
  equalsIcon: {
    composes: '$icon'
  },
  valueFieldContent: {
    marginTop: 0
  },
  dot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    border: `2px solid ${theme.colorPalette.orange_50}`,
    background: theme.colorPalette.beige_50,
    borderRadius: '50%'
  },
  timelineDot: {
    left: 0,
    position: 'absolute',
    zIndex: 5
  },
  segmentDot: ({ isInPolicyPreviewMode, active, isLocked }) => ({
    // The active (selected) segment bullet is filled orange; others stay beige.
    // Set here (a dynamic rule) rather than only on activeDot, because this
    // function style is injected after the static activeDot rule and would
    // otherwise override its orange fill back to beige.
    background: active ? theme.colorPalette.orange_50 : theme.colorPalette.beige_50,
    width: 20,
    height: 20,
    left: -1,
    cursor: isLocked || isInPolicyPreviewMode ? 'default' : 'pointer',
    padding: 0,
    // Locked segments are non-interactive: no hover ring animation.
    '&:hover': isLocked
      ? {}
      : {
          boxShadow: `0px 0px 0px 10px ${theme.colorPalette.orange_50_op_08}`,
          background: active ? theme.colorPalette.orange_50 : theme.colorPalette.beige_50
        }
  }),
  segmentsListItem: ({ isInPolicyPreviewMode, isDraft, isMutual }) => ({
    paddingLeft: 0,
    // Mutual keeps the whole item readable (the date must stay legible) and dims only
    // the card body via lockedContent; Branch keeps the legacy whole-item dim.
    opacity: !isMutual && isInPolicyPreviewMode && !isDraft ? 0.6 : 1
  }),
  vItemCard: {
    padding: 24,
    boxShadow: '0 0 2px 0 rgba(0,0,0,0.12), 0 2px 2px 0 rgba(0,0,0,0.24)',
    marginBottom: 7
  },
  vItemCardDraft: {
    padding: 0,
    overflow: 'hidden',
    border: `2px dashed ${theme.colorPalette.orange_50}`,
    borderRadius: 6,
    backgroundClip: 'padding-box'
  },
  draftHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 6),
    backgroundColor: theme.colorPalette.beige_25
  },
  draftHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  draftLabelGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  draftDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.colorPalette.beige_90
  },
  draftHeaderText: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.33,
    textTransform: 'uppercase',
    color: theme.colorPalette.beige_90
  },
  draftHelpIcon: {
    width: 12,
    height: 12,
    marginLeft: 0,
    color: theme.colorPalette.orange_50
  },
  draftMenuButton: {
    padding: 0
  },
  draftMenuItem: {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.33,
    color: theme.colorPalette.green_90
  },
  draftMenuIcon: {
    width: 24,
    height: 24
  },
  draftCardContent: {
    padding: 24
  },
  vItemSegment: {
    marginBottom: 24
  },
  segmentsList: {
    paddingLeft: 0,
    margin: 0,
    listStyle: 'none'
  },
  // Mirrors a draft segment's dashed track and dot so the summary reads as part of the history timeline.
  draftsSummary: {
    display: 'flex'
  },
  draftsSummaryTracker: {
    position: 'relative',
    width: 11,
    marginRight: 24,
    borderRight: `2px dashed ${theme.colorPalette.orange_50}`,
    flexShrink: 0
  },
  draftsSummaryDot: {
    position: 'absolute',
    left: 0,
    width: 18,
    height: 18,
    border: `2px solid ${theme.colorPalette.orange_50}`,
    borderRadius: '50%',
    background: theme.colorPalette.beige_50,
    zIndex: 5
  },
  draftsSummaryContent: {
    width: '100%',
    paddingBottom: theme.spacing(6)
  },
  draftsSummaryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3)
  },
  seeDraftsButton: {
    font: 'inherit',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.33,
    color: theme.colorPalette.orange_50,
    background: 'none',
    border: 'none',
    borderRadius: 4,
    padding: theme.spacing(2),
    cursor: 'pointer',
    '&:hover': {
      background: theme.colorPalette.orange_50_op_10
    }
  },
  draftsSummaryCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    marginTop: theme.spacing(3),
    padding: theme.spacing(3, 4),
    border: `2px dashed ${theme.colorPalette.orange_50}`,
    borderRadius: 6,
    backgroundClip: 'padding-box'
  },
  draftsSummaryText: {
    color: theme.colorPalette.green_100
  },
  draftsSummaryIcon: {
    width: 32,
    height: 32
  },
  segmentsContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  },
  segmentsBottomWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    marginRight: 24
  },
  segmentsBottomLine: {
    display: 'inline-block',
    width: 11,
    height: 16
  },
  segmentTimelineTracker: ({ isDraft, isInPolicyPreviewMode }) => ({
    position: 'relative',
    width: 11,
    marginRight: 24,
    borderRight: `2px ${isDraft && isInPolicyPreviewMode ? 'dashed' : 'solid'} ${theme.colorPalette.orange_50}`,
    display: ' inline-block'
  }),
  segmentContent: ({ isInPolicyPreviewMode }) => ({
    width: '100%',
    cursor: isInPolicyPreviewMode ? 'default' : 'pointer'
  }),
  activeDot: {
    background: theme.colorPalette.orange_50,
    boxShadow: '0 8px 8px 0 rgba(0, 0, 0, 0.24), 0 0 8px 0 rgba(0, 0, 0, 0.12)',
    '&:hover': {
      boxShadow: '0 8px 8px 0 rgba(0, 0, 0, 0.24), 0 0 8px 0 rgba(0, 0, 0, 0.12)',
      background: theme.colorPalette.orange_50
    }
  },
  activeCard: {
    boxShadow: '0 8px 8px 0 rgba(0, 0, 0, 0.24), 0 0 8px 0 rgba(0, 0, 0, 0.12)'
  },
  firstContainer: {
    marginBottom: 26
  },
  changed: {
    marginBottom: theme.sizes.footerHeight + 50
  },
  clarionDoorLink: {
    color: theme.palette.secondary.main,
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  mutualRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  mutualRowLabel: {
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.43,
    flexShrink: 0,
    color: theme.colorPalette.green_75
  },
  // Block (not Label's flex) so a long value can truncate with an ellipsis.
  mutualRowValue: {
    display: 'block',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.43,
    textAlign: 'right',
    color: theme.colorPalette.green_80
  },
  // Only the Policy Premium value is emphasized (SemiBold) per Figma Body2_Semibold;
  // Created by / Created values stay regular (400).
  mutualRowValueEmphasis: {
    fontWeight: 600
  },
  mutualDivider: {
    border: 'none',
    borderTop: `1px solid ${theme.colorPalette.beige_50_op_50}`,
    margin: theme.spacing(2, 0, 4)
  },
  timelineSubtitleMutual: {
    color: theme.colorPalette.green_100,
    fontSize: 14,
    fontWeight: 700,
    textTransform: 'none',
    marginBottom: 8,
    marginTop: 3,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  lockIcon: {
    width: 16,
    height: 16
  },
  lockedContent: {
    opacity: 0.5
  },
  // Dim the timeline dot + connector line for locked segments so they don't
  // look clickable (the date + lock icon stay at full opacity).
  lockedTimeline: {
    opacity: 0.5
  }
});

export default makeStyles(styles);
